import { ArrowRight, LoaderCircle } from 'lucide-react'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import type {
  CredentialStatusResponse,
  CredentialValidationResponse,
} from './types'

interface CredentialValidationPanelProps {
  credential?: CredentialStatusResponse
  validation?: CredentialValidationResponse
  isLoading: boolean
  isError: boolean
  onRetry: () => void
}

export function CredentialValidationPanel({
  credential,
  validation,
  isLoading,
  isError,
  onRetry,
}: CredentialValidationPanelProps) {
  if (credential?.status !== 'configured') return null

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="font-medium text-primary-high-contrast">Validation</h3>
      {!credential.can_review_credentials ? (
        <p className="mt-2 text-sm text-muted-foreground">
          Validation details are available to Careforce staff.
        </p>
      ) : isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <LoaderCircle className="size-4 animate-spin" />
          Loading validation details…
        </div>
      ) : isError ? (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-muted-foreground">
            The validation details could not be loaded.
          </p>
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        </div>
      ) : validation?.state === 'passed' ? (
        <div className="mt-3 space-y-2">
          <p className="text-sm text-success-solid">Passed</p>
          {validation.session_id && (
            <Link
              to={`/sessions/${validation.session_id}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-solid hover:underline">
              View validation session
              <ArrowRight className="size-3.5" />
            </Link>
          )}
        </div>
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          No passed validation details are available for the active credential
          version.
        </p>
      )}
    </div>
  )
}
